import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { POPOVER_ID_DATA_ATTR, POPOVER_REGEX } from '@examdojo/tiptap-popover';
import escape from 'lodash/escape';
import { MarkdownModule } from 'ngx-markdown';
import { MarkdownBlock } from '../../../model';
import { POPOVER_TRIGGER_CLASS } from '../../inline-renderers/popover-trigger-host/popover-trigger-host.component';
import { generateHeadingId, HEADING_REGEX, TOC_ID_DATA_ATTR } from '../../table-of-contents';

@Component({
  selector: 'dojo-markdown-block-renderer',
  templateUrl: './markdown-block-renderer.component.html',
  styleUrl: './markdown-block-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MarkdownModule],
})
export class MarkdownBlockRendererComponent {
  readonly block = input.required<MarkdownBlock>();

  readonly katexOptions = {
    throwOnError: false,
    strict: 'warn' as const,
  };

  readonly processedMarkdown = computed(() => {
    const block = this.block();
    let markdown = block.markdown;
    markdown = this.addHeadingIds(markdown, block.id);
    markdown = this.transformPopoverSyntaxToHtml(markdown);
    return markdown;
  });

  /**
   * Adds data-toc-id spans before each heading for TOC navigation.
   * The span is placed before the heading so it can be used as a scroll target.
   */
  private addHeadingIds(markdown: string, blockId: string): string {
    let headingIndex = 0;
    return markdown.replace(HEADING_REGEX, (match) => {
      const id = generateHeadingId(blockId, headingIndex++);
      return `<span ${TOC_ID_DATA_ATTR}="${id}"></span>\n${match}`;
    });
  }

  /**
   * Transforms popover syntax {{popover:id}}content{{/popover}} into HTML spans
   * that render inline within the markdown content.
   */
  private transformPopoverSyntaxToHtml(markdown: string): string {
    return markdown.replace(POPOVER_REGEX, (_match, popoverId: string, content: string) => {
      return `<span class="${POPOVER_TRIGGER_CLASS}" ${POPOVER_ID_DATA_ATTR}="${escape(popoverId)}">${content}</span>`;
    });
  }
}
